// ============================================================================
// DATA VALIDATION UTILITY
// Schema validation for ExecutionStep data from local generators with fallback values
// Ensures graceful degradation when generators produce invalid or incomplete data
// ============================================================================

import {
  ExecutionStep,
  StepType,
  StepMetadata,
  StepPayload,
  GraphStatePayload,
  ArrayStatePayload,
  MatrixStatePayload,
  TreeStatePayload,
  StringStatePayload,
  GenericStatePayload,
  ArrayItem,
  Vertex,
  Edge,
  TreeNode,
  TreeEdge,
} from '../types/rust-engine-interfaces';

export interface ValidationResult {
  isValid: boolean;
  data: ExecutionStep;
  warnings: string[];
  errors: string[];
}

export interface ValidatorConfig {
  strictMode?: boolean;
  enableFallbacks?: boolean;
  logWarnings?: boolean;
  logErrors?: boolean;
}

const DEFAULT_CONFIG: Required<ValidatorConfig> = {
  strictMode: false,
  enableFallbacks: true,
  logWarnings: true,
  logErrors: true,
};

/**
 * Main validator for ExecutionStep data from local generators
 * Provides comprehensive validation with fallback values for graceful degradation
 */
export class DataValidator {
  private config: Required<ValidatorConfig>;

  constructor(config: ValidatorConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Validate a single ExecutionStep
   */
  validateExecutionStep(step: unknown): ValidationResult {
    const warnings: string[] = [];
    const errors: string[] = [];

    // Check if step is an object
    if (!step || typeof step !== 'object' || Array.isArray(step)) {
      const error = 'ExecutionStep must be an object';
      errors.push(error);
      if (this.config.logErrors) {
        console.error('[DataValidator] Invalid step structure:', step);
      }
      return {
        isValid: false,
        data: this.createFallbackStep(),
        warnings,
        errors,
      };
    }

    const stepObj = step as Record<string, unknown>;
    const validatedStep: Partial<ExecutionStep> = {};

    // Validate stepId
    if (typeof stepObj.stepId === 'number' && stepObj.stepId >= 0) {
      validatedStep.stepId = stepObj.stepId;
    } else {
      warnings.push('Missing or invalid stepId, using fallback');
      validatedStep.stepId = 0;
    }

    // Validate stepType
    if (this.isValidStepType(stepObj.stepType)) {
      validatedStep.stepType = stepObj.stepType as StepType;
    } else {
      warnings.push('Missing or invalid stepType, using fallback');
      validatedStep.stepType = StepType.StateChange;
    }

    // Validate timestamp
    if (typeof stepObj.timestamp === 'number' && stepObj.timestamp > 0) {
      validatedStep.timestamp = stepObj.timestamp;
    } else {
      warnings.push('Missing or invalid timestamp, using fallback');
      validatedStep.timestamp = Date.now();
    }

    // Validate metadata
    const metadataResult = this.validateMetadata(stepObj.metadata);
    validatedStep.metadata = metadataResult.data;
    warnings.push(...metadataResult.warnings);
    errors.push(...metadataResult.errors);

    // Validate payload
    const payloadResult = this.validatePayload(stepObj.payload, validatedStep.stepType!);
    validatedStep.payload = payloadResult.data;
    warnings.push(...payloadResult.warnings);
    errors.push(...payloadResult.errors);

    const isValid = errors.length === 0;

    if (warnings.length > 0 && this.config.logWarnings) {
      console.warn('[DataValidator] Validation warnings:', warnings);
    }

    if (errors.length > 0 && this.config.logErrors) {
      console.error('[DataValidator] Validation errors:', errors);
    }

    return {
      isValid,
      data: validatedStep as ExecutionStep,
      warnings,
      errors,
    };
  }

  /**
   * Validate multiple ExecutionSteps
   */
  validateExecutionSteps(steps: unknown[]): ValidationResult[] {
    if (!Array.isArray(steps)) {
      console.error('[DataValidator] Invalid steps data: expected array');
      return [
        {
          isValid: false,
          data: this.createFallbackStep(),
          warnings: [],
          errors: ['Steps must be an array'],
        },
      ];
    }

    return steps.map((step) => this.validateExecutionStep(step));
  }

  /**
   * Validate metadata object
   */
  private validateMetadata(metadata: unknown): {
    data: StepMetadata;
    warnings: string[];
    errors: string[];
  } {
    const warnings: string[] = [];
    const errors: string[] = [];
    const validatedMetadata: StepMetadata = {};

    if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
      warnings.push('Missing or invalid metadata, using empty metadata');
      return { data: validatedMetadata, warnings, errors };
    }

    const metadataObj = metadata as Record<string, unknown>;

    // Validate line (optional)
    if (typeof metadataObj.line === 'number' && metadataObj.line >= 0) {
      validatedMetadata.line = metadataObj.line;
    }

    // Validate complexity (optional)
    if (typeof metadataObj.complexity === 'string') {
      validatedMetadata.complexity = metadataObj.complexity;
    }

    // Validate explanation (optional)
    if (typeof metadataObj.explanation === 'string') {
      validatedMetadata.explanation = metadataObj.explanation;
    }

    // Validate highlightIds (optional)
    if (Array.isArray(metadataObj.highlightIds)) {
      const validIds = metadataObj.highlightIds.filter(
        (id): id is string => typeof id === 'string'
      );
      if (validIds.length !== metadataObj.highlightIds.length) {
        warnings.push('Some highlightIds were invalid and filtered');
      }
      validatedMetadata.highlightIds = validIds;
    }

    return { data: validatedMetadata, warnings, errors };
  }

  /**
   * Validate payload based on step type
   */
  private validatePayload(
    payload: unknown,
    stepType: StepType
  ): {
    data: StepPayload;
    warnings: string[];
    errors: string[];
  } {
    const warnings: string[] = [];
    const errors: string[] = [];

    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      errors.push('Payload must be an object');
      return {
        data: this.createFallbackPayload(stepType),
        warnings,
        errors,
      };
    }

    const payloadObj = payload as Record<string, unknown>;
    const payloadType = payloadObj.type;

    // Validate based on payload type
    switch (payloadType) {
      case 'graph':
        return this.validateGraphPayload(payloadObj);
      case 'array':
        return this.validateArrayPayload(payloadObj);
      case 'matrix':
        return this.validateMatrixPayload(payloadObj);
      case 'tree':
        return this.validateTreePayload(payloadObj);
      case 'string':
        return this.validateStringPayload(payloadObj);
      case 'generic':
        return this.validateGenericPayload(payloadObj);
      default:
        warnings.push(`Unknown payload type "${payloadType}", using generic fallback`);
        return {
          data: this.createFallbackPayload(stepType),
          warnings,
          errors,
        };
    }
  }

  /**
   * Validate graph state payload
   */
  private validateGraphPayload(payload: Record<string, unknown>): {
    data: GraphStatePayload;
    warnings: string[];
    errors: string[];
  } {
    const warnings: string[] = [];
    const errors: string[] = [];

    const validatedPayload: Partial<GraphStatePayload> = {
      type: 'graph',
    };

    // Validate vertices
    if (Array.isArray(payload.vertices)) {
      validatedPayload.vertices = payload.vertices
        .map((v) => this.validateVertex(v))
        .filter((v): v is Vertex => v !== null);
      
      if (validatedPayload.vertices.length !== payload.vertices.length) {
        warnings.push('Some vertices were invalid and filtered');
      }
    } else {
      errors.push('Missing or invalid vertices array');
      validatedPayload.vertices = [];
    }

    // Validate edges
    if (Array.isArray(payload.edges)) {
      const validatedEdges = payload.edges
        .map((e) => this.validateEdge(e))
        .filter((e): e is Edge => e !== null);
      const vertexIds = new Set((validatedPayload.vertices ?? []).map((v) => v.id));
      const knownEdges = validatedEdges.filter((e) => vertexIds.has(e.from) && vertexIds.has(e.to));
      validatedPayload.edges = knownEdges;

      if (validatedEdges.length !== payload.edges.length) {
        warnings.push('Some edges were invalid and filtered');
      }
      if (knownEdges.length !== validatedEdges.length) {
        warnings.push('Some edges referenced unknown vertices and were dropped');
      }
    } else {
      warnings.push('Missing or invalid edges array');
      validatedPayload.edges = [];
    }

    // Validate metadata
    const graphMetadata = this.validateGraphMetadata(payload.metadata);
    validatedPayload.metadata = graphMetadata.data;
    warnings.push(...graphMetadata.warnings);
    errors.push(...graphMetadata.errors);

    return {
      data: validatedPayload as GraphStatePayload,
      warnings,
      errors,
    };
  }

  /**
   * Validate array state payload
   */
  private validateArrayPayload(payload: Record<string, unknown>): {
    data: ArrayStatePayload;
    warnings: string[];
    errors: string[];
  } {
    const warnings: string[] = [];
    const errors: string[] = [];

    const validatedPayload: Partial<ArrayStatePayload> = {
      type: 'array',
    };

    // Validate items
    if (Array.isArray(payload.items)) {
      validatedPayload.items = payload.items
        .map((item) => this.validateArrayItem(item))
        .filter((item): item is ArrayItem => item !== null);
      
      if (validatedPayload.items.length !== payload.items.length) {
        warnings.push('Some array items were invalid and filtered');
      }
    } else {
      errors.push('Missing or invalid items array');
      validatedPayload.items = [];
    }

    // Validate metadata
    const arrayMetadata = this.validateArrayMetadata(payload.metadata);
    validatedPayload.metadata = arrayMetadata.data;
    warnings.push(...arrayMetadata.warnings);
    errors.push(...arrayMetadata.errors);

    return {
      data: validatedPayload as ArrayStatePayload,
      warnings,
      errors,
    };
  }

  /**
   * Validate matrix state payload
   */
  private validateMatrixPayload(payload: Record<string, unknown>): {
    data: MatrixStatePayload;
    warnings: string[];
    errors: string[];
  } {
    const warnings: string[] = [];
    const errors: string[] = [];

    const validatedPayload: Partial<MatrixStatePayload> = {
      type: 'matrix',
    };

    // Validate rows
    if (typeof payload.rows === 'number' && payload.rows > 0) {
      validatedPayload.rows = payload.rows;
    } else {
      warnings.push('Missing or invalid rows, using fallback');
      validatedPayload.rows = 1;
    }

    // Validate cols
    if (typeof payload.cols === 'number' && payload.cols > 0) {
      validatedPayload.cols = payload.cols;
    } else {
      warnings.push('Missing or invalid cols, using fallback');
      validatedPayload.cols = 1;
    }

    // Validate data: preserve row length, coerce non-numeric cells to 0.
    if (Array.isArray(payload.data)) {
      const validData = payload.data
        .map((row) => {
          if (Array.isArray(row)) {
            return row.map((cell) =>
              typeof cell === 'number' || typeof cell === 'string' ? cell : 0
            );
          }
          return null;
        })
        .filter((row): row is (number | string)[] => row !== null);
      
      validatedPayload.data = validData;
      
      if (validatedPayload.data.length !== payload.data.length) {
        warnings.push('Some matrix rows were invalid and filtered');
      }
    } else {
      errors.push('Missing or invalid data array');
      validatedPayload.data = [[0]];
    }

    // Validate metadata
    const matrixMetadata = this.validateMatrixMetadata(payload.metadata);
    validatedPayload.metadata = matrixMetadata.data;
    warnings.push(...matrixMetadata.warnings);
    errors.push(...matrixMetadata.errors);

    return {
      data: validatedPayload as MatrixStatePayload,
      warnings,
      errors,
    };
  }

  /**
   * Validate tree state payload
   */
  private validateTreePayload(payload: Record<string, unknown>): {
    data: TreeStatePayload;
    warnings: string[];
    errors: string[];
  } {
    const warnings: string[] = [];
    const errors: string[] = [];

    const validatedPayload: Partial<TreeStatePayload> = {
      type: 'tree',
    };

    // Validate nodes
    if (Array.isArray(payload.nodes)) {
      validatedPayload.nodes = payload.nodes
        .map((node) => this.validateTreeNode(node))
        .filter((node): node is TreeNode => node !== null);
      
      if (validatedPayload.nodes.length !== payload.nodes.length) {
        warnings.push('Some tree nodes were invalid and filtered');
      }
    } else {
      errors.push('Missing or invalid nodes array');
      validatedPayload.nodes = [];
    }

    // Validate edges
    if (Array.isArray(payload.edges)) {
      validatedPayload.edges = payload.edges
        .map((edge) => this.validateTreeEdge(edge))
        .filter((edge): edge is TreeEdge => edge !== null);
      
      if (validatedPayload.edges.length !== payload.edges.length) {
        warnings.push('Some tree edges were invalid and filtered');
      }
    } else {
      warnings.push('Missing or invalid edges array');
      validatedPayload.edges = [];
    }

    // Validate metadata
    const treeMetadata = this.validateTreeMetadata(payload.metadata);
    validatedPayload.metadata = treeMetadata.data;
    warnings.push(...treeMetadata.warnings);
    errors.push(...treeMetadata.errors);

    return {
      data: validatedPayload as TreeStatePayload,
      warnings,
      errors,
    };
  }

  /**
   * Validate string state payload
   */
  private validateStringPayload(payload: Record<string, unknown>): {
    data: StringStatePayload;
    warnings: string[];
    errors: string[];
  } {
    const warnings: string[] = [];
    const errors: string[] = [];

    const validatedPayload: Partial<StringStatePayload> = {
      type: 'string',
    };

    // Validate value
    if (typeof payload.value === 'string') {
      validatedPayload.value = payload.value;
    } else {
      warnings.push('Missing or invalid string value, using fallback');
      validatedPayload.value = '';
    }

    // Validate metadata
    const stringMetadata = this.validateStringMetadata(payload.metadata);
    validatedPayload.metadata = stringMetadata.data;
    warnings.push(...stringMetadata.warnings);
    errors.push(...stringMetadata.errors);

    return {
      data: validatedPayload as StringStatePayload,
      warnings,
      errors,
    };
  }

  /**
   * Validate generic state payload
   */
  private validateGenericPayload(payload: Record<string, unknown>): {
    data: GenericStatePayload;
    warnings: string[];
    errors: string[];
  } {
    const warnings: string[] = [];
    const errors: string[] = [];

    const validatedPayload: Partial<GenericStatePayload> = {
      type: 'generic',
    };

    // Validate data
    if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) {
      validatedPayload.data = payload.data as Record<string, unknown>;
    } else {
      warnings.push('Missing or invalid generic data, using empty object');
      validatedPayload.data = {};
    }

    // Validate metadata
    if (payload.metadata && typeof payload.metadata === 'object' && !Array.isArray(payload.metadata)) {
      validatedPayload.metadata = payload.metadata as Record<string, unknown>;
    } else {
      warnings.push('Missing or invalid generic metadata, using empty object');
      validatedPayload.metadata = {};
    }

    return {
      data: validatedPayload as GenericStatePayload,
      warnings,
      errors,
    };
  }

  /**
   * Validate vertex object
   */
  private validateVertex(vertex: unknown): Vertex | null {
    if (!vertex || typeof vertex !== 'object' || Array.isArray(vertex)) {
      return null;
    }

    const vertexObj = vertex as Record<string, unknown>;

    if (typeof vertexObj.id !== 'string') {
      return null;
    }

    if (
      typeof vertexObj.value !== 'string' &&
      typeof vertexObj.value !== 'number'
    ) {
      return null;
    }

    const validatedVertex: Vertex = {
      id: vertexObj.id,
      value: vertexObj.value as string | number,
      label: typeof vertexObj.label === 'string' ? vertexObj.label : String(vertexObj.value),
    };

    // Copy all optional fields through — visualizers handle missing/invalid
    // fields gracefully. Whitelisting was stripping position, etc.
    for (const key of Object.keys(vertexObj)) {
      if (key === 'id' || key === 'value' || key === 'label') continue;
      (validatedVertex as unknown as Record<string, unknown>)[key] = vertexObj[key];
    }

    return validatedVertex;
  }

  /**
   * Validate edge object
   */
  private validateEdge(edge: unknown): Edge | null {
    if (!edge || typeof edge !== 'object' || Array.isArray(edge)) {
      return null;
    }

    const edgeObj = edge as Record<string, unknown>;

    if (typeof edgeObj.from !== 'string' || typeof edgeObj.to !== 'string') {
      return null;
    }

    const validatedEdge: Edge = {
      from: edgeObj.from,
      to: edgeObj.to,
    };

    // Copy all optional fields through — flow, capacity, residual, label,
    // id, etc. were being stripped by the whitelist.
    for (const key of Object.keys(edgeObj)) {
      if (key === 'from' || key === 'to') continue;
      (validatedEdge as unknown as Record<string, unknown>)[key] = edgeObj[key];
    }

    return validatedEdge;
  }

  /**
   * Validate array item object
   */
  private validateArrayItem(item: unknown): ArrayItem | null {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      return null;
    }

    const itemObj = item as Record<string, unknown>;

    if (typeof itemObj.index !== 'number' || itemObj.index < 0) {
      return null;
    }

    if (
      typeof itemObj.value !== 'string' &&
      typeof itemObj.value !== 'number'
    ) {
      return null;
    }

    const validatedItem: ArrayItem = {
      index: itemObj.index,
      value: itemObj.value as string | number,
    };

    // Optional boolean flags
    if (typeof itemObj.highlighted === 'boolean') {
      validatedItem.highlighted = itemObj.highlighted;
    }
    if (typeof itemObj.compared === 'boolean') {
      validatedItem.compared = itemObj.compared;
    }
    if (typeof itemObj.swapped === 'boolean') {
      validatedItem.swapped = itemObj.swapped;
    }
    if (typeof itemObj.sorted === 'boolean') {
      validatedItem.sorted = itemObj.sorted;
    }

    return validatedItem;
  }

  /**
   * Validate tree node object
   */
  private validateTreeNode(node: unknown): TreeNode | null {
    if (!node || typeof node !== 'object' || Array.isArray(node)) {
      return null;
    }

    const nodeObj = node as Record<string, unknown>;

    if (typeof nodeObj.id !== 'string') {
      return null;
    }

    if (
      typeof nodeObj.value !== 'string' &&
      typeof nodeObj.value !== 'number'
    ) {
      return null;
    }

    const validatedNode: TreeNode = {
      id: nodeObj.id,
      value: nodeObj.value as string | number,
    };

    // Copy all optional fields through — children (needed for trie/B-tree),
    // balanceFactors, and other fields were being stripped by the whitelist.
    for (const key of Object.keys(nodeObj)) {
      if (key === 'id' || key === 'value') continue;
      (validatedNode as unknown as Record<string, unknown>)[key] = nodeObj[key];
    }

    return validatedNode;
  }

  /**
   * Validate tree edge object
   */
  private validateTreeEdge(edge: unknown): TreeEdge | null {
    if (!edge || typeof edge !== 'object' || Array.isArray(edge)) {
      return null;
    }

    const edgeObj = edge as Record<string, unknown>;

    if (typeof edgeObj.from !== 'string' || typeof edgeObj.to !== 'string') {
      return null;
    }

    const validatedEdge: TreeEdge = {
      from: edgeObj.from,
      to: edgeObj.to,
    };

    // Optional type field
    if (edgeObj.type === 'left' || edgeObj.type === 'right' || edgeObj.type === 'parent') {
      validatedEdge.type = edgeObj.type;
    }

    return validatedEdge;
  }

  /**
   * Validate graph metadata
   */
  private validateGraphMetadata(metadata: unknown): {
    data: Record<string, unknown>;
    warnings: string[];
    errors: string[];
  } {
    const warnings: string[] = [];
    const errors: string[] = [];

    if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
      warnings.push('Missing or invalid graph metadata, using empty object');
      return { data: {}, warnings, errors };
    }

    // Pass through all metadata fields — visualizers handle missing/invalid
    // fields gracefully. Whitelisting here was stripping critical fields
    // (queue, traversalTreeEdges, traversalOrder, path, startVertex, mstEdges,
    // predecessors, etc.) that generators emit and visualizers depend on.
    return { data: metadata as Record<string, unknown>, warnings, errors };
  }

  /**
   * Validate array metadata
   */
  private validateArrayMetadata(metadata: unknown): {
    data: Record<string, unknown>;
    warnings: string[];
    errors: string[];
  } {
    const warnings: string[] = [];
    const errors: string[] = [];

    if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
      warnings.push('Missing or invalid array metadata, using empty object');
      return { data: {}, warnings, errors };
    }

    // Pass through all metadata fields — visualizers handle missing/invalid
    // fields gracefully. Whitelisting here was stripping fields that some
    // generators emit (e.g. custom overlay metadata).
    return { data: metadata as Record<string, unknown>, warnings, errors };
  }

  /**
   * Validate matrix metadata
   */
  private validateMatrixMetadata(metadata: unknown): {
    data: Record<string, unknown>;
    warnings: string[];
    errors: string[];
  } {
    const warnings: string[] = [];
    const errors: string[] = [];

    if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
      warnings.push('Missing or invalid matrix metadata, using empty object');
      return { data: {}, warnings, errors };
    }

    // Pass through all metadata fields.
    return { data: metadata as Record<string, unknown>, warnings, errors };
  }

  /**
   * Validate tree metadata
   */
  private validateTreeMetadata(metadata: unknown): {
    data: Record<string, unknown>;
    warnings: string[];
    errors: string[];
  } {
    const warnings: string[] = [];
    const errors: string[] = [];

    if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
      warnings.push('Missing or invalid tree metadata, using empty object');
      return { data: {}, warnings, errors };
    }

    // Pass through all metadata fields — balanceFactors and other
    // algorithm-specific metadata were being stripped by the whitelist.
    return { data: metadata as Record<string, unknown>, warnings, errors };
  }

  /**
   * Validate string metadata
   */
  private validateStringMetadata(metadata: unknown): {
    data: Record<string, unknown>;
    warnings: string[];
    errors: string[];
  } {
    const warnings: string[] = [];
    const errors: string[] = [];

    if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
      warnings.push('Missing or invalid string metadata, using empty object');
      return { data: {}, warnings, errors };
    }

    // Pass through all metadata fields — pattern, mismatchedIndices,
    // windowStart, windowEnd, scanIndex, failureFunction, hashValue,
    // zValues, palindromeRadii, etc. were being stripped by the whitelist.
    return { data: metadata as Record<string, unknown>, warnings, errors };
  }

  /**
   * Check if a value is a valid StepType enum value
   */
  private isValidStepType(value: unknown): boolean {
    const validTypes = Object.values(StepType);
    return typeof value === 'string' && validTypes.includes(value as StepType);
  }

  /**
   * Create a fallback ExecutionStep for when validation fails completely
   */
  private createFallbackStep(): ExecutionStep {
    return {
      stepId: 0,
      stepType: StepType.StateChange,
      timestamp: Date.now(),
      payload: this.createFallbackPayload(StepType.StateChange),
      metadata: {},
    };
  }

  /**
   * Create a fallback payload based on step type
   */
  private createFallbackPayload(_stepType: StepType): StepPayload {
    // Generic fallback preserves category semantics and surfaces the issue instead of pretending it's an array.
    return {
      type: 'generic',
      data: { error: 'Unsupported payload type for this step' },
      metadata: {},
    } as GenericStatePayload;
  }
}

// ============================================================================
// FACTORY FUNCTIONS
// ============================================================================

/**
 * Create a validator instance with default configuration
 */
export function createDataValidator(config?: ValidatorConfig): DataValidator {
  return new DataValidator(config);
}

/**
 * Validate a single step with default configuration
 */
export function validateStep(step: unknown): ValidationResult {
  const validator = new DataValidator();
  return validator.validateExecutionStep(step);
}

/**
 * Validate multiple steps with default configuration
 */
export function validateSteps(steps: unknown[]): ValidationResult[] {
  const validator = new DataValidator();
  return validator.validateExecutionSteps(steps);
}

/**
 * Get valid steps only (filter out invalid ones)
 */
export function getValidSteps(steps: unknown[]): ExecutionStep[] {
  const results = validateSteps(steps);
  return results
    .filter((result) => result.isValid)
    .map((result) => result.data);
}

/**
 * Get all steps with fallbacks applied (include invalid ones with fallback data)
 */
export function getStepsWithFallbacks(steps: unknown[]): ExecutionStep[] {
  const validator = new DataValidator({ enableFallbacks: true });
  return validator.validateExecutionSteps(steps).map((result) => result.data);
}
